/*
  In-app calendar sign-in panel.

  Drops into the Add / Edit Calendar modals in place of the "paste the base64
  string CLAP printed" step. Handles consent, surfaces the result, and lists the
  calendars discovered on the account so the resource calendar ID can be copied
  into the meeting space form without a trip to the console.

  Deliberately styled with sx / inline styles rather than makeStyles: this app
  runs @material-ui/core v4 and @mui/material v5 side by side, and both mint
  makeStyles class names in the same namespace (see the note in src/index.js).
  Not declaring any avoids that hazard entirely.
*/

import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import LoginIcon from "@mui/icons-material/Login";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  runCalendarOAuth,
  isOAuthCalendarType,
  providerLabel,
} from "../utils/calendarOAuth";

const CalendarList = ({ calendars }) => {
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(value);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (e) {
      // Clipboard is blocked outside a secure context; the ID is on screen
      // either way, so this is a convenience, not a failure.
    }
  };

  if (!calendars || calendars.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, display: "block", mb: 1 }}
      >
        Calendars on this account
      </Typography>
      <Box
        sx={{
          maxHeight: 180,
          overflowY: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {calendars.map((calendar) => (
          <Box
            key={calendar.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.75,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&:last-of-type": { borderBottom: "none" },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={calendar.name}
              >
                {calendar.name}
                {calendar.canEdit === false ? " (read-only)" : ""}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={calendar.id}
              >
                {calendar.groupName ? `${calendar.groupName} · ` : ""}
                {calendar.id}
              </Typography>
            </Box>
            <Tooltip title={copiedId === calendar.id ? "Copied" : "Copy ID"}>
              <IconButton size="small" onClick={() => copy(calendar.id)}>
                {copiedId === calendar.id ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * @param {string}   calendarType   "Google Calendar" | "Office 365"
 * @param {string}   credentialName name for a new credential
 * @param {string}   calendarId     credential to re-authenticate, instead of a name
 * @param {string}   emmsId         selected EMMS id, for the piggyback proxy
 * @param {object}   emmsHeaders    emmsheaders?.emmsheaders?.headers
 * @param {object}   requestConfig  axios config from Redux state.header
 * @param {function} onSignedIn     called with the result once EMMS has saved the credential
 * @param {boolean}  signedIn       whether the credential is already saved
 * @param {boolean}  reauthenticate renders as a re-authenticate action
 * @param {boolean}  disabled
 */
const CalendarOAuthPanel = ({
  calendarType,
  emmsId,
  emmsHeaders,
  requestConfig,
  credentialName,
  calendarId,
  onSignedIn,
  signedIn: alreadySignedIn = false,
  reauthenticate = false,
  disabled = false,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  if (!isOAuthCalendarType(calendarType)) return null;

  const provider = providerLabel(calendarType);

  const handleSignIn = async () => {
    setError("");
    setBusy(true);
    try {
      // runCalendarOAuth opens the popup before its first await, so this stays
      // inside the user's click gesture and the popup blocker leaves it alone.
      const outcome = await runCalendarOAuth({
        calendarType,
        credentialName,
        calendarId,
        emmsId,
        emmsHeaders,
        requestConfig,
      });
      setResult(outcome);
      if (typeof onSignedIn === "function") {
        onSignedIn(outcome);
      }
    } catch (e) {
      const detail =
        e?.response?.data ||
        e?.message ||
        "Sign-in failed. Please try again.";
      setError(typeof detail === "string" ? detail : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const signedIn = Boolean(result) || Boolean(alreadySignedIn);
  const label = busy
    ? "Waiting for sign-in…"
    : signedIn
    ? `Re-authenticate with ${provider}`
    : `Sign in with ${provider}`;

  return (
    <Box sx={{ my: 1.5 }}>
      <Button
        variant={signedIn ? "outlined" : "contained"}
        color="primary"
        onClick={handleSignIn}
        disabled={busy || disabled}
        fullWidth
        startIcon={
          busy ? (
            <CircularProgress size={16} color="inherit" />
          ) : signedIn || reauthenticate ? (
            <RefreshIcon />
          ) : (
            <LoginIcon />
          )
        }
        sx={{ textTransform: "none" }}
      >
        {label}
      </Button>

      <Collapse in={Boolean(error)}>
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={Boolean(result)}>
        {result ? (
          <Alert
            severity={result.hasRefreshToken ? "success" : "warning"}
            sx={{ mt: 1.5 }}
          >
            {result.hasRefreshToken ? (
              "Signed in."
            ) : (
              <>
                <AlertTitle sx={{ fontSize: "0.85rem" }}>
                  No refresh token
                </AlertTitle>
                Syncing will stop in about an hour. Remove the app's access in
                your {provider} account and sign in again.
              </>
            )}
            <CalendarList calendars={result.calendars} />
          </Alert>
        ) : (
          <span />
        )}
      </Collapse>
    </Box>
  );
};

export default CalendarOAuthPanel;
